import { runtimeConfig } from './config';
import { tr, trMaybe } from '../i18n';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

type TokenGetter = () => Promise<string | null>;
let getToken: TokenGetter = async () => null;
let onUnauthorized: () => void = () => {};

export function configureApi(opts: { getToken: TokenGetter; onUnauthorized: () => void }) {
  getToken = opts.getToken;
  onUnauthorized = opts.onUnauthorized;
}

export function apiUrl(path: string): string {
  return `${runtimeConfig().apiUrl}${path}`;
}

export function buildQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

async function request<T>(method: string, path: string, body?: unknown, opts: { auth?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (opts.auth !== false) {
    const token = await getToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }
  let res: Response;
  try {
    res = await fetch(apiUrl(path), { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'network_error', tr('common.networkError'));
  }
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const e = (json as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    if (res.status === 401 && opts.auth !== false) onUnauthorized();
    throw new ApiError(res.status, e?.code ?? 'http_error', e?.message ?? tr('common.httpError', { status: res.status }), e?.details);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, opts?: { auth?: boolean }) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body: unknown = {}, opts?: { auth?: boolean }) => request<T>('POST', path, body, opts),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  /** Descarga autenticada (p. ej. exportación CSV). */
  async download(path: string, fallbackName: string) {
    const token = await getToken();
    const res = await fetch(apiUrl(path), { headers: token ? { authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new ApiError(res.status, 'http_error', tr('common.httpError', { status: res.status }));
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
    saveBlob(await res.blob(), name);
  },
};

export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Mensaje para el usuario: se traduce por código de error; si no hay traducción, el del servidor. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return trMaybe(`errors.${err.code}`) ?? err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

// ---------------------------------------------------------------- tipos de la API

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface Product {
  id: string;
  code: string;
  name: string;
  description: string | null;
  offline_grace_days: number;
  refresh_interval_hours: number;
  floating_lease_minutes: number;
  offline_activation_days: number;
  trial_enabled: boolean;
  trial_days: number;
  trial_features: string[];
  default_features: string[];
  created_at: string;
}
export interface Feature {
  id: string;
  code: string;
  name: string;
  description: string | null;
}
export interface Edition {
  id: string;
  product_id: string;
  code: string;
  name: string;
  description: string | null;
  features: string[];
  limits: Record<string, number>;
  sort_order: number;
  license_count?: number;
}
export const editionsOf = (p: ProductDetail | null | undefined): Edition[] => p?.editions ?? [];

export interface ProductVersion {
  id: string;
  version: string;
  released_at: string;
  notes: string | null;
}
export interface ProductDetail extends Product {
  features: Feature[];
  /** Ausente si la API es anterior a las ediciones: usa `editionsOf`. */
  editions?: Edition[];
  versions: ProductVersion[];
  license_count: number;
}
export interface Customer {
  id: string;
  name: string;
  email: string | null;
  tax_id: string | null;
  country: string | null;
  notes: string | null;
  external_ref: string | null;
  created_at: string;
}
export interface CustomerUser {
  id: string;
  email: string;
  name: string | null;
}
export interface License {
  id: string;
  key: string;
  product_id: string;
  product_code: string | null;
  product_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  // Opcionales: una API anterior a las ediciones no los envía.
  edition_id?: string | null;
  edition_code?: string | null;
  edition_name?: string | null;
  type: 'perpetual' | 'subscription' | 'trial';
  mode: 'node_locked' | 'floating';
  status: 'active' | 'suspended' | 'revoked' | 'expired';
  seats: number;
  features: string[];
  limits: Record<string, number>;
  max_version: string | null;
  expires_at: string | null;
  updates_until: string | null;
  offline_grace_days: number | null;
  suspended_reason: string | null;
  notes: string | null;
  active_activations?: number;
  /** Última conexión de cualquiera de sus equipos; `null` = nunca activada. Ausente en webhooks y APIs anteriores. */
  last_seen_at?: string | null;
  /** Todos sus equipos activos se activaron por fichero (no se espera que se conecten). */
  offline_only?: boolean;
  created_at: string;
}
export interface Activation {
  id: string;
  license_id: string;
  fingerprint: string;
  machine_name: string | null;
  os: string | null;
  app_version: string | null;
  ip: string | null;
  offline: boolean;
  activated_at: string;
  last_seen_at: string;
  deactivated_at: string | null;
  deactivated_reason: string | null;
  active: boolean;
  license_key?: string;
  product_code?: string;
  customer_name?: string | null;
  license_status?: string;
}
export interface Lease {
  id: string;
  fingerprint: string;
  checked_out_at: string;
  last_heartbeat_at: string;
  expires_at: string;
  released_at: string | null;
  active: boolean;
}
/** Suscripción de Stripe de una licencia comprada desde la app. */
export interface LicenseSubscription {
  plan: string | null;
  status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  dashboard_url: string | null;
}
/** Factura de una venta desde la app (cola hacia Holded). */
export interface InvoiceRecord {
  id: string;
  kind: 'invoice' | 'creditnote';
  source_id: string;
  status: 'pending' | 'issued' | 'draft' | 'failed' | 'skipped';
  attempts: number;
  last_error: string | null;
  tax_case: string | null;
  amount_total: number | null;
  currency: string | null;
  external_number: string | null;
  created_at: string;
  issued_at: string | null;
}
export interface LicenseDetail extends License {
  activations: Activation[];
  leases: Lease[];
  subscription: LicenseSubscription | null;
  invoices: InvoiceRecord[];
}
export interface AuditEntry {
  id: string;
  at: string;
  actor_type: string;
  actor_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  license_id: string | null;
  ip: string | null;
  data: Record<string, unknown>;
}
export interface Webhook {
  id: string;
  url: string;
  description: string | null;
  events: string[];
  enabled: boolean;
  secret: string;
  created_at: string;
}
export interface Delivery {
  id: string;
  event: string;
  status: string;
  attempts: number;
  last_error: string | null;
  response_status: number | null;
  created_at: string;
  delivered_at: string | null;
}
export interface Dashboard {
  licenses_active: number;
  activations_active: number;
  floating_sessions_active: number;
  licenses_expiring_30d: number;
  // Opcionales: una API anterior no los envía.
  licenses_inactive_30d?: number;
  inactive_30d?: License[];
  by_product: { product_id: string; code: string; name: string; active: number; total: number }[];
  recent_activations: Activation[];
  expiring_soon: License[];
}
export interface AuthConfig {
  local: boolean;
  oidc: { issuer: string; client_id: string; scopes: string; token_type: string } | null;
}
